import React, { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'en' | 'zh';

export const translations = {
  en: {
    nav: {
      features: "Features",
      pricing: "Pricing",
      resources: "Resources",
      login: "Log in",
      signup: "Sign up free",
      dashboard: "Dashboard"
    },
    landing: {
      pill: "The First Meeting Minutes AI Agent",
      heroTitle: "Transform Every Deep Meeting into Precise Structured Knowledge.",
      heroSubtitle: "Utilizing a multi-expert Agent engine tuned for professional scenarios. Zero hallucinations, bi-directional traceability, and precise capture of fleeting insights.",
      cta: "Start Free Trial",
      mockup: {
        sourceTitle: "Raw Transcript Source",
        reportTitle: "Professional Report View",
        windowTitle: "Minutes AI - Professional Report Generator",
        sarahName: "Sarah (VP)",
        sarahText: "We need to finalize the Q3 infrastructure budget. The cloud costs are spiking.",
        davidName: "David (CTO)",
        davidText: "The main bottleneck is the legacy SQL database. If we scale to 50k users, latency will hit 2 seconds. We must prioritize the Redis caching layer migration immediately.",
        mikeName: "Mike (Product)",
        mikeText: "Does that affect the feature roadmap for next month?",
        docTitle: "Technical Infrastructure Review",
        preparedBy: "Prepared by: Minutes AI",
        summaryTitle: "Executive Summary",
        summaryText: "The team discussed Q3 budget allocation and infrastructure scaling requirements. Key focus areas include cost optimization and legacy system upgrades.",
        riskTitle: "Risk Assessment & Mitigation",
        scalabilityTitle: "Scalability Bottlenecks",
        sqlLimit: "Legacy SQL Limitations: Current architecture cannot support projected user growth (50k+).",
        latencyRisk: "Latency Risk: Projected latency >2s at peak load without intervention.",
        actionTitle: "Required Action",
        highPriority: "High Priority",
        redisAction: "Immediate implementation of Redis Caching Layer to offload database queries and reduce latency.",
        sourceTag: "Source: David (10:12)"
      },
      feature1Title: "Expert-Level Synthesis",
      feature1Desc: "Our model understands complex meeting logic, separating signal from noise to generate consulting-grade minutes. No fluff, just structure.",
      feature2Title: "Zero Hallucinations",
      feature2Desc: "Every bullet point links back to the exact source audio. Bi-directional traceability ensures total trust.",
      feature3Title: "Workflow Freedom",
      feature3Desc: "Use McKinsey, Agile, or custom templates. Export to Word/PDF instantly.",
      feature4Title: "Enterprise-Grade Security",
      feature4Desc: "Your data is isolated and encrypted. We don't train on your voice without permission. SOC2 Compliant.",
      bento: {
        rawAudioLog: "Raw Audio Log",
        rawLogLine1: "\"Regarding 2025 planning, we should focus on the dormant users.\"",
        rawLogLine2: "\"Agreed. We have years of behavioral data we aren't using. Can we use AI for personalized re-engagement?\"",
        rawLogLine3: "\"Yes, that's exactly the move. Let's make AI-driven User Reactivation our top priority.\"",
        strategicPlan: "Strategic Plan 2025",
        shortTermPriority: "Short-Term Priority",
        reactivationTitle: "Focus 2025 key initiatives on User Reactivation.",
        reactivationDesc: "Leverage accumulated behavioral data to drive targeted re-engagement campaigns.",
        aiApps: "Expand AI Applications for personalization.",
        transcriptHeader: "Raw Transcript",
        alexName: "Alex (CFO)",
        alexText: "TAM is approaching $10B. What's driving this?",
        sarahRole: "Sarah (VP Product)",
        sarahText: "It's the pro tier. Consumer demand for pro features is exceeding expectations.",
        marketAnalysis: "Market Analysis",
        marketScale: "Market Scale",
        scaleValue: "Total addressable market approaching $10B.",
        growthDriver: "Growth Driver",
        driverText: "Robust consumer demand sustains a high growth rate.",
        verification: "Verification",
        sourceMatched: "Source Matched"
      },
      testimonialsTitle: "Loved by efficient teams",
      testimonialsSub: "Join 10,000+ professionals saving time every day.",
      testimonials: [
        { name: "Sarah J.", role: "Product Manager", text: "I stopped taking notes manually. Minutes AI captures every detail and the auto-tagging is genius." },
        { name: "David L.", role: "CTO @ StartUp", text: "The bidirectional linking between transcripts and my notes changed how I review architecture meetings." },
        { name: "Elena R.", role: "UX Researcher", text: "Finally, a tool that understands context. The summaries aren't just generic bullet points." },
        { name: "Marcus T.", role: "Consultant", text: "It pays for itself in one meeting. The ROI calculator on the site is actually conservative." },
        { name: "Jessica W.", role: "Marketing Lead", text: "Deep search across all my voice notes is a superpower I didn't know I needed." },
        { name: "Tom H.", role: "Founder", text: "Cleanest interface in the game. No clutter, just value." }
      ],
      partnersTitle: "Trusted by forward-thinking companies",
      calcTitle: "Calculate your efficiency gains",
      calcDesc: "See exactly how much time and budget Minutes AI can recover for your organization by automating high-fidelity note-taking.",
      teamSize: "Team Size",
      meetingsWeek: "Meetings / Week",
      hourlyRate: "Avg. Hourly Rate",
      annualSavings: "Annual Savings",
      hoursReclaimed: "Hours Reclaimed",
      infoFidelity: "Info Fidelity",
      footerDesc: "The high-fidelity AI workspace for professionals who demand precision, trust, and efficiency."
    },
    dashboard: {
      team: "Minutes AI Team",
      main: "MAIN",
      allRecordings: "All Tasks",
      recent: "Recent",
      favorites: "Favorites",
      folders: "FOLDERS",
      upgrade: "Upgrade to Pro",
      viewPlan: "View my plan",
      freeLabel: "Free",
      proLabel: "Pro",
      planSuffix: "Plan",
      minUsed: "已使用 210 / 300 分钟",
      invite: "Invite & Earn",
      inviteSub: "Get 60 min free",
      logout: "Log out",
      sessionKickedTitle: "Signed Out",
      sessionKickedMessage: "Your account has been signed in on another device. You have been signed out of this session.",
      sessionExpiredTitle: "Session Expired",
      sessionExpiredMessage: "Your session has expired. Please sign in again.",
      sessionConfirm: "Sign In Again",
      searchPlaceholder: "Search transcripts...",
      newRecording: "New Task",
      processing: "Processing",
      proUsed: "已使用 510 / 600 分钟",
      noTasksInFolder: "No files in this folder",
      dragToMove: "Drag files here"
    },
    deleteModal: {
      deleteTitle: "Delete",
      deleteDescription: "You are about to permanently delete",
      cannotUndo: "This action cannot be undone.",
      cancel: "No, Keep It",
      confirm: "Yes, Delete!"
    },
    settings: {
      bindPhone: "Bind Phone",
      bindEmail: "Bind Email",
      phoneNumber: "Phone Number",
      emailAddress: "Email Address",
      verificationCode: "Verification Code",
      sendCode: "Send Code",
      resend: "Resend",
      confirmBind: "Confirm Binding",
      bindSuccess: "Binding successful",
      enterPhone: "Enter phone number",
      enterEmail: "Enter email",
      enterCode: "Enter code",
      bindDesc: "Can be used for login and password recovery"
    },
    workspace: {
      back: "Workspace",
      processed: "Processed",
      template: "Template",
      saved: "Saved",
      save: "Save",
      download: "Download",
      share: "Share",
      transcript: "Transcript",
      aiSummary: "AI Summary",
      readOnly: "Read Only",
      saving: "Saving...",
      loading: "Loading",
      loadingEllipsis: "Loading...",
      fetchFailed: "Failed to load data",
      editNotes: "Edit Notes",
      backButton: "Back",
      searchTranscript: "Search transcript...",
      clickEditSpeaker: "Click to edit speaker",
      editSpeaker: "Edit Speaker",
      enterSpeakerName: "Enter speaker name",
      renameAllSpeaker: "Rename all",
      cancel: "Cancel",
      confirm: "OK",
      switchToChinese: "Switch to Chinese",
      switchToEnglish: "Switch to English",
      preview: "Notes Preview",
      original: "Original",
      edited: "Edited",
      savedStatus: "Saved",
      saveEdits: "Save edits",
      savedCheck: "✓ Saved",
      reset: "Reset",
      exportWordTitle: "Export current content as Word",
      exporting: "Exporting...",
      exportWord: "Export Word",
      exportFailed: "Export failed",
      tutorialAlt: "Editor tutorial",
      tutorialText: "Drag the left handle to reorder paragraphs. Click the right link button to view related source text. Hold from blank area to multi-select.",
      dontRemind: "Don't remind",
      gotIt: "Got it",
      chineseNumbers: "I, II, III",
      viewLinkedSource: "View linked source",
      inputPlaceholder: "Type here..."
    },
    templateCenter: {
      title: "Template Center",
      customizing: "Customizing",
      autoSaved: "Auto-saved",
      done: "Done",
      hierarchyLevels: "Hierarchy Levels",
      levelPrefix: "Level",
      addLevel: "Add Level",
      removeLevel: "Remove Level",
      listFormat: "List Format",
      none: "None",
      number: "Number",
      bullet: "Bullet",
      numberStyle: "Number Style",
      suffix: "Suffix",
      empty: "Empty",
      typography: "Typography",
      fontEastAsia: "Font (East Asia)",
      fontLatin: "Font (Latin)",
      sizeStyle: "Size & Style",
      layoutSpacing: "Layout & Spacing",
      symbolPosition: "Symbol Position",
      textGap: "Text Gap",
      spaceBefore: "Space Before",
      spaceAfter: "Space After",
      unitLines: "lines",
      unitCh: "ch",
      symbol: "Symbol",
      custom: "Custom",
      textIndent: "Text Indent",
      lineSpacing: "Line Spacing",
      lineSingle: "Single (1.0)",
      lineWordDefault: "1.08x (Word Default)",
      line12: "1.2x",
      line15: "1.5x",
      lineDouble: "Double (2.0)",
      schema: "SCHEMA",
      levels: "LEVELS",
      presetTemplates: "Preset Templates",
      myTemplates: "My Templates",
      newTemplate: "New",
      rename: "Rename",
      delete: "Delete",
      confirmDelete: "Delete this template?",
      renameTitle: "Rename Template",
      createTitle: "New Template",
      saveAsTitle: "Save As New Template",
      inputPlaceholder: "Enter template name",
      cancel: "Cancel",
      confirm: "OK",
      creating: "Creating...",
      create: "Create",
      save: "Save",
      saving: "Saving...",
      saved: "Saved",
      apply: "Apply Template",
      applied: "Applied",
      showNotes: "Show Edited Notes",
      showStyle: "Show Template Style",
      showOriginal: "Show Original Notes",
      templateActions: "Template Actions"
    },
    newModal: {
      title: "New File",
      subtitle: "Upload a file or start capturing instantly.",
      sourceFile: "SOURCE FILE",
      upload: "Click to upload",
      dragDrop: "or drag and drop",
      fileLimit: "MP3, M4A, WAV up to 200MB",
      contextTitle: "Context Details",
      estimatedCost: "Estimated Cost",
      usageDeduction: "Usage Deduction",
      costUnit: "Hours",
      deductionUnit: "Mins",
      company: "Company Name",
      industry: "Industry",
      scenarioTitle: "Select Scenario",
      scenarios: {
        podcast: { title: "Podcast", sub: "Interviews, Conversations", desc: "Ideal for podcast interviews and conversations. Auto-extracts key points, speaker insights and discussion highlights." },
        finance: { title: "Financial Interview", sub: "Due diligence, Earnings", desc: "Optimized for earnings calls & due diligence. Highlights numerical data, YoY comparisons & sentiment analysis." },
        qa: { title: "Q&A", sub: "Panels, AMAs, FAQs", desc: "Separates questions from answers automatically. Great for panels, AMAs, and user research sessions." },
        course: { title: "Course", sub: "Classes, Lectures", desc: "Structured for educational content. Generates summaries, key takeaways, and study guides." }
      },
      contextInputs: {
        podcast: { label1: "Content", ph1: "e.g. Meeting agenda, key topics" },
        finance: { label1: "Industry", label2: "Company Name", label3: "Content", ph1: "e.g. Fintech, SaaS", ph2: "e.g. Acme Corp", ph3: "e.g. Q3 earnings call" },
        qa: { label1: "Content", ph1: "e.g. Q4 All Hands, Roadmap discussion" },
        course: { label1: "Content", ph1: "e.g. Macroeconomics lecture notes" }
      },
      autoDetect: "AI will auto-detect speakers based on the selected scenario.",
      startBtn: "Start Transcribing",
      targetLang: "Target Language",
      comingSoon: "Developing more templates... Custom templates coming soon."
    },
    pricing: {
      title: "Choose Your Plan",
      subtitle: "Start for free, scale as you grow.",
      monthly: "Monthly",
      yearly: "Yearly",
      save20: "Save 20%",
      mostPopular: "Popular",
      bestDeal: "Best Deal",
      getStarted: "Start Free Trial",
      startFree: "Buy Now",
      contactSales: "Book Demo",
      annualSaveTag: "Save $96/year",
      teamDiscount: "-20% Team Discount",
      enterpriseOnboarding: "Enterprise Onboarding Included",
      trustTitle: "Enterprise-Grade Trust.",
      trustDesc: "Join 2,500+ top-tier consulting and engineering teams. We offer SOC2 compliance and zero data training without explicit consent.",
      currencySymbol: "$",
      priceAnchor: "As low as $2.40 / hr (Save 40%)",
      prices: {
        free: "0",
        pro: { monthly: "39.9", yearly: "31.9" },
        team: { monthly: "59", yearly: "47.2" },
        addons: {
          single: "3.99",
          value: { price: "35.00", originalPrice: "39.90", detail: "($3.5/hr)" },
          business: { price: "149.00", originalPrice: "199.50", detail: "($2.98/hr)" }
        }
      },
      plans: {
        basic: {
            name: "Free",
            desc: "Suitable for personal users for light usage."
        },
        pro: {
            name: "Pro",
            desc: "Built for professionals chasing ultimate efficiency."
        },
        team: {
            name: "Team",
            desc: "For teams requiring collaboration and organizational asset accumulation."
        }
      },
      features: {
        basic: [
          "60 min processing time/mo",
          "Top AI Models (Powered by GPT-5.2, Claude 4.5 & Gemini 3.0 Pro)",
          "Meeting-grade Agent capabilities",
          "Smart Bi-directional Audio-Text linking",
          "10 Cloud meeting storage limit"
        ],
        pro: [
          "1,000 min Priority processing/mo",
          "Includes all Free plan capabilities",
          "Ready-to-Deliver Word export",
          "Unlimited Cloud document storage",
          "Customizable Agent templates"
        ],
        team: [
          "Includes all Pro plan features",
          "Team Knowledge Base: Cross-meeting Agent deep search",
          "Shared Agent Template library: Unified delivery standards",
          "Admin control & Team efficiency reports"
        ]
      },
      addons: {
        title: "Need more time?",
        desc: "Top up as needed, valid forever.",
        single: "Single Top-up",
        value: "Value Pack (600m)",
        business: "Business Pack (3000m)",
        popular: "Most Popular"
      },
      roi: {
          title: "The 5x Productivity Multiplier",
          desc: "Typically, 1 hour of recording takes 5 hours to organize manually. With Minutes AI, you save those 5 hours instantly.",
          manual: "Manual Work",
          ai: "With Minutes AI",
          highlight: "Save 5 hours per meeting hour"
      }
    },
    auth: {
      welcomeBack: "Welcome Back",
      welcomeSub: "Continue organizing your world with AI-powered clarity.",
      joinTitle: "Join Minutes AI",
      joinSub: "Experience the new standard of professional efficiency.",
      marketing: {
        zeroHallucinations: "Zero hallucinations",
        enterpriseSecurity: "Enterprise security",
        unlimitedRecording: "Unlimited recording"
      },
      login: "Log in",
      createAccount: "Create Account",
      createSub: "Start your 14-day free trial. No credit card required.",
      email: "Email",
      phone: "Phone",
      mobileNumber: "Mobile Number",
      verificationCode: "Verification Code",
      sendCode: "Send Code",
      codeSent: "Code Sent",
      resendIn: "Resend in",
      password: "Password",
      setPassword: "Set Password",
      fullName: "Full Name",
      inviteCode: "Invitation Code (Optional)",
      getCode: "GET 1 MONTH FREE",
      orContinue: "Or continue with",
      noAccount: "Don't have an account?",
      hasAccount: "Already have an account?",
      signup: "Sign Up",
      loginTabs: {
        password: "Password",
        emailCode: "Email Code",
        phoneCode: "Phone Code"
      },
      accountPlaceholder: "Email / Phone / Username",
      forgotPassword: "Forgot?",
      forgotPasswordTitle: "Reset Password",
      forgotPasswordDesc: "Enter your email or phone to receive a verification code",
      resetMethod: {
        email: "Reset via Email",
        phone: "Reset via Phone"
      },
      newPassword: "New Password",
      confirmPassword: "Confirm Password",
      resetPassword: "Reset Password",
      backToLogin: "Back to Login",
      passwordResetSuccess: "Password reset successfully",
      whatsNew: "What's New",
      march2025: "March 2025",
      systemsOperational: "Systems Operational",
      insightsWaiting: "Your meeting insights are waiting for you.",
      releaseNotes: "View Release Notes",
      consultingEngineTitle: "Consulting-Grade Synthesis v2.4",
      consultingEngineDesc: "Our latest reasoning engine upgrade significantly improves technical terminology accuracy and multi-speaker overlapping detection.",
      testimonials: [
        {
          name: "Justin Ozanich",
          role: "Marketing Manager",
          company: "Foster Coffee Company",
          text: "With Minutes AI I can build social content out as far as I want into the future but also tailor campaigns if we see certain trends within the industry.",
          avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=150&auto=format&fit=crop"
        },
        {
          name: "Elena Rodriguez",
          role: "Legal Counsel",
          company: "TechFlow Systems",
          text: "The absolute fidelity and zero hallucinations give me the confidence to use these summaries in professional filings without manual checking.",
          avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=150&auto=format&fit=crop"
        }
      ]
    },
    gateModal: {
      title: "Join Minutes AI Today",
      subtitle: "Create unlimited structured minutes for your work with our rich collection of templates.",
      createAccount: "Create Your Account",
      orSignIn: "Or Sign In"
    },
    inviteModal: {
        title: "Invite Friends, Get 60 Min",
        desc: "Invite friends to register, both get 60 min free!",
        yourCode: "Your Invite Code",
        tapToCopy: "TAP TO COPY",
        copied: "COPIED",
        orShareLink: "Or share link",
        terms: "Terms & Conditions apply",
        referrals: "0 referrals so far"
    },
    share: {
      title: "Share Recording",
      publicAccess: "Public Access",
      publicDesc: "Anyone with the link can view this transcript.",
      privateDesc: "Only invited team members can access.",
      copyLink: "Copy Link",
      copied: "Copied",
      preview: "Preview",
      previewTooltip: "See what they see",
      canView: "Can view",
      canEdit: "Can edit",
      disclaimer: "By sharing this link, you agree to our Terms of Service regarding content distribution."
    },
    public: {
      sharedBy: "Shared by",
      join: "Join the conversation",
      joinDesc: "Sign up to edit, comment, and export this meeting.",
      signupFree: "Sign Up Free",
      wantAction: "Want to see the action items?",
      login: "Log in",
      signup: "Sign Up"
    }
  },
  zh: {
    nav: {
      features: "功能特性",
      pricing: "价格方案",
      resources: "资源中心",
      login: "登录",
      signup: "免费注册",
      dashboard: "控制台"
    },
    landing: {
      pill: "首款会议纪要 AI Agent",
      heroTitle: "把每一场深度会议转化为精确的结构化知识",
      heroSubtitle: "我们使用针对专业场景调优的多专家Agent引擎，实现 0 幻觉、信息双向溯源，精准捕捉转瞬即逝的关键信息。",
      cta: "开始免费试用",
      mockup: {
        sourceTitle: "原始转写流",
        reportTitle: "专业级报告视图",
        windowTitle: "Minutes AI - 专业级会议纪要生成器",
        sarahName: "Sarah (副总裁)",
        sarahText: "我们需要最终确定 Q3 的基础设施预算. 云成本正在飙升。",
        davidName: "David (首席技术官)",
        davidText: "核心瓶颈在于当前的 SQL 数据库集群架构. 根据压力测试结果，一旦用户规模突破 5 万大关，查询延迟将飙升至 2 秒以上。我们必须立即将 Redis 缓存层的迁移列为 Q3 最高优先级任务。",
        mikeName: "Mike (产品经理)",
        mikeText: "这会影响下个月的功能路线图吗？",
        docTitle: "技术基础设施架构评审",
        preparedBy: "生成方：Minutes AI",
        summaryTitle: "执行摘要",
        summaryText: "本次会议核心探讨了 Q3 季度的预算分配细节与基础设施的长期扩展需求. 管理层达成共识，将重点投入成本结构优化以及老旧系统的平滑升级，为接下来的爆发增长期做好充分支撑。",
        riskTitle: "风险评估与缓解方案",
        scalabilityTitle: "可扩展性瓶颈",
        sqlLimit: "旧版 SQL 限制：当前架构无法支持预计的 5 万+ 用户增长。",
        latencyRisk: "延迟风险：若不干预，峰值负载下的预计延迟将超过 2 秒。",
        actionTitle: "必要行动项",
        highPriority: "高优先级",
        redisAction: "立即实施 Redis 缓存层以减轻数据库查询压力并降低延迟。",
        sourceTag: "来源：David (10:12)"
      },
      feature1Title: "专家级综合",
      feature1Desc: "我们的模型理解复杂的会议逻辑，从噪音中分离信号，生成咨询级会议纪要. 没有废话，只有结构。",
      feature2Title: "零幻觉",
      feature2Desc: "每一个要点都能链接回原始音频. 双向可溯源性确保了完全的信任。",
      feature3Title: "工作流自由",
      feature3Desc: "使用麦肯锡、敏捷开发或自定义模板. 即时导出为 Word/PDF。",
      feature4Title: "企业级安全",
      feature4Desc: "您的数据是隔离和加密的. 未经许可，我们不会使用您的语音进行训练。符合 SOC2 标准。",
      bento: {
        rawAudioLog: "原始音频日志",
        rawLogLine1: "“关于 2025 年的规划，我觉得核心焦点应该放在老用户的促活上。”",
        rawLogLine2: "“同意. 我们积累了多年的行为数据一直没用上. 能不能用 AI 做精准的个性化再触达？”",
        rawLogLine3: "“没错，这正是我们要做的. 把‘AI 驱动的用户激活’列为年度最高优先级。”",
        strategicPlan: "2025 战略规划",
        shortTermPriority: "短期优先级",
        reactivationTitle: "2025 年核心计划：用户激活。",
        reactivationDesc: "利用积累的行为数据驱动精准的再触达营销活动。",
        aiApps: "扩大 AI 在个性化推荐中的应用。",
        transcriptHeader: "原始逐字稿",
        alexName: "Alex (首席财务官)",
        alexText: "可触达市场规模正逼近 100 亿美元. 是什么在驱动这一点？",
        sarahRole: "Sarah (产品副总裁)",
        sarahText: "是专业版层级. 消费者对专业功能的需求超出了预期。",
        marketAnalysis: "市场分析报告",
        marketScale: "市场规模",
        scaleValue: "总可触达市场规模接近 100 亿美元。",
        growthDriver: "增长驱动因素",
        driverText: "强劲的消费者需求支撑了高增长率。",
        verification: "真实性验证",
        sourceMatched: "已匹配原始数据源"
      },
      testimonialsTitle: "深受高效团队喜爱",
      testimonialsSub: "加入 10,000+ 专业人士，每天节省时间。",
      testimonials: [
        { name: "Sarah J.", role: "产品经理", text: "我不再手动做笔记了. Minutes AI 捕捉了每一个细节，自动打标签功能简直天才。" },
        { name: "David L.", role: "初创公司 CTO", text: "逐字稿和笔记之间的双向链接改变了我复盘架构会议的方式。" },
        { name: "Elena R.", role: "用户研究员", text: "终于有一个能理解语境的工具了. 摘要不再是那种泛泛而谈的要点。" },
        { name: "Marcus T.", role: "咨询顾问", text: "开一次会就回本了. 网站上的 ROI 计算器其实算得很保守。" },
        { name: "Jessica W.", role: "营销负责人", text: "在所有语音笔记中进行深度搜索是我以前不敢想象的超能力。" },
        { name: "Tom H.", role: "创始人", text: "业内最简洁的界面. 没有冗余，全是价值。" }
      ],
      partnersTitle: "深受全球前沿企业信赖",
      calcTitle: "计算您的效率收益",
      calcDesc: "看看 Minutes AI 通过自动化高保真笔记，能为您的组织挽回多少时间和预算。",
      teamSize: "团队规模",
      meetingsWeek: "每周会议",
      hourlyRate: "平均时薪",
      annualSavings: "年度节省",
      hoursReclaimed: "挽回工时",
      infoFidelity: "信息保真度",
      footerDesc: "专为追求精准、信任和效率的专业人士打造的高保真 AI 工作台。"
    },
    dashboard: {
      team: "Minutes AI 团队",
      main: "主要",
      allRecordings: "全部任务",
      recent: "最近",
      favorites: "收藏",
      folders: "文件夹",
      upgrade: "升级专业版",
      viewPlan: "查看我的计划",
      freeLabel: "Free",
      proLabel: "Pro",
      planSuffix: "计划",
      minUsed: "已使用 210 / 300 分钟",
      invite: "邀请有礼",
      inviteSub: "获赠 60 分钟时长",
      logout: "退出登录",
      sessionKickedTitle: "账号已下线",
      sessionKickedMessage: "您的账号已在其他设备登录，当前会话已失效。",
      sessionExpiredTitle: "登录已过期",
      sessionExpiredMessage: "您的登录状态已过期，请重新登录。",
      sessionConfirm: "重新登录",
      searchPlaceholder: "搜索逐字稿...",
      newRecording: "新建任务",
      processing: "处理中",
      proUsed: "已使用 510 / 600 分钟",
      noTasksInFolder: "此文件夹暂无文件",
      dragToMove: "拖拽文件到此处"
    },
    deleteModal: {
      deleteTitle: "删除",
      deleteDescription: "您即将永久删除",
      cannotUndo: "此操作无法撤销。",
      cancel: "取消",
      confirm: "确认删除"
    },
    settings: {
      bindPhone: "绑定手机号",
      bindEmail: "绑定邮箱",
      phoneNumber: "手机号",
      emailAddress: "邮箱地址",
      verificationCode: "验证码",
      sendCode: "发送验证码",
      resend: "重新发送",
      confirmBind: "确认绑定",
      bindSuccess: "绑定成功",
      enterPhone: "请输入手机号",
      enterEmail: "请输入邮箱",
      enterCode: "请输入验证码",
      bindDesc: "绑定后可用于登录和找回密码"
    },
    workspace: {
      back: "工作台",
      processed: "已处理",
      template: "模板",
      saved: "已保存",
      save: "保存",
      download: "下载",
      share: "分享",
      transcript: "逐字稿",
      aiSummary: "AI 智能摘要",
      readOnly: "只读模式",
      saving: "保存中...",
      loading: "加载中",
      loadingEllipsis: "加载中...",
      fetchFailed: "获取数据失败",
      editNotes: "编辑纪要",
      backButton: "返回",
      searchTranscript: "搜索逐字稿...",
      clickEditSpeaker: "点击编辑说话人",
      editSpeaker: "编辑说话人",
      enterSpeakerName: "输入说话人名称",
      renameAllSpeaker: "修改所有",
      cancel: "取消",
      confirm: "确认",
      switchToChinese: "切换到中文",
      switchToEnglish: "切换到英文",
      preview: "纪要预览",
      original: "原始版",
      edited: "编辑版",
      savedStatus: "已保存",
      saveEdits: "保存编辑内容",
      savedCheck: "✓ 已保存",
      reset: "重置",
      exportWordTitle: "导出当前内容为Word文档",
      exporting: "导出中...",
      exportWord: "导出Word",
      exportFailed: "导出失败",
      tutorialAlt: "编辑器教学",
      tutorialText: "拖拽左侧手柄可调整段落顺序，点击右侧连线按钮可查看关联原文。从空白处按住可多选。",
      dontRemind: "不再提醒",
      gotIt: "知道了",
      chineseNumbers: "一, 二, 三",
      viewLinkedSource: "查看关联原文",
      inputPlaceholder: "输入内容..."
    },
    templateCenter: {
      title: "模板中心",
      customizing: "当前编辑",
      autoSaved: "已自动保存",
      done: "完成",
      hierarchyLevels: "层级设置",
      levelPrefix: "层级",
      addLevel: "添加层级",
      removeLevel: "移除层级",
      listFormat: "列表格式",
      none: "无",
      number: "编号",
      bullet: "项目符号",
      numberStyle: "编号样式",
      suffix: "后缀",
      empty: "无",
      typography: "字体排版",
      fontEastAsia: "中文字体",
      fontLatin: "西文字体",
      sizeStyle: "大小与样式",
      layoutSpacing: "布局与间距",
      symbolPosition: "符号位置",
      textGap: "文本间距",
      spaceBefore: "段前间距",
      spaceAfter: "段后间距",
      unitLines: "行",
      unitCh: "字符",
      symbol: "符号",
      custom: "自定义",
      textIndent: "文本缩进",
      lineSpacing: "行距",
      lineSingle: "单倍 (1.0)",
      lineWordDefault: "1.08倍 (Word 默认)",
      line12: "1.2倍",
      line15: "1.5倍",
      lineDouble: "双倍 (2.0)",
      schema: "模板方案",
      levels: "层级",
      presetTemplates: "预设模板",
      myTemplates: "我的模板",
      newTemplate: "新建模板",
      rename: "重命名",
      delete: "删除",
      confirmDelete: "确定删除此模板？",
      renameTitle: "重命名模板",
      createTitle: "新建模板",
      saveAsTitle: "另存为新模板",
      inputPlaceholder: "输入模板名称",
      cancel: "取消",
      confirm: "确定",
      creating: "创建中...",
      create: "创建",
      save: "保存",
      saving: "保存中...",
      saved: "已保存",
      apply: "应用模板",
      applied: "已应用",
      showNotes: "展示编辑版纪要",
      showStyle: "展示模板样式",
      showOriginal: "展示原始版纪要",
      templateActions: "模板操作"
    },
    newModal: {
      title: "新建任务",
      subtitle: "上传文件或立即开始录制。",
      sourceFile: "原始文件",
      upload: "点击上传",
      dragDrop: "或拖拽文件至此",
      fileLimit: "支持 MP3, M4A, WAV 最大 200MB",
      contextTitle: "上下文详情",
      estimatedCost: "预计消耗",
      usageDeduction: "扣除时长",
      costUnit: "小时",
      deductionUnit: "分钟",
      company: "公司名称",
      industry: "所属行业",
      scenarioTitle: "从选择你的模板开始吧...",
      scenarios: {
        podcast: { title: "播客", sub: "访谈, 对话", desc: "适合播客访谈和对话场景。自动提取关键观点、嘉宾见解和讨论要点。" },
        finance: { title: "金融访谈", sub: "尽职调查, 财报分析", desc: "针对财报电话会议和尽职调查进行优化。突出显示数值数据、同比比较和情绪分析。" },
        qa: { title: "问答", sub: "论坛, 访谈", desc: "自动分离问题与回答。非常适合专家面板、AMA 问答和用户研究访谈。" },
        course: { title: "课程", sub: "公开课, 讲座", desc: "专为教育内容结构化设计。生成摘要、关键知识点和学习指南。" }
      },
      contextInputs: {
        podcast: { label1: "内容", ph1: "例如：会议议程、关键话题" },
        finance: { label1: "所属行业", label2: "公司名称", label3: "内容", ph1: "例如：金融科技, SaaS", ph2: "例如：Acme Corp", ph3: "例如：Q3财报电话会" },
        qa: { label1: "内容", ph1: "例如：Q4 全员大会、路线图讨论" },
        course: { label1: "内容", ph1: "例如：宏观经济课程笔记" }
      },
      autoDetect: "AI 将根据所选场景自动检测发言人。",
      startBtn: "开始转写",
      targetLang: "输出目标语言",
      comingSoon: "更多模板正在开发中... 自定义模板即将上线。"
    },
    pricing: {
      title: "简单，透明的定价",
      subtitle: "免费开始，随需扩展。",
      monthly: "月付",
      yearly: "年付",
      save20: "节省 20%",
      mostPopular: "最受欢迎",
      bestDeal: "最划算",
      getStarted: "开始免费试用",
      startFree: "购买",
      contactSales: "联系销售",
      annualSaveTag: "每年立省 ¥324",
      teamDiscount: "-20% 团队优惠",
      enterpriseOnboarding: "包含企业级入职服务",
      trustTitle: "企业级信任保障",
      trustDesc: "加入 2,500+ 顶尖咨询与工程团队. 我们提供 SOC2 合规保障，且在未经许可的情况下绝不使用您的数据进行 AI 训练。",
      currencySymbol: "¥",
      priceAnchor: "低至 ¥8.4 / 小时 (对比单买节省 40%)",
      prices: {
        free: "0",
        pro: { monthly: "139", yearly: "112" },
        team: { monthly: "199", yearly: "159" },
        addons: {
          single: "13.9",
          value: { price: "119.0", originalPrice: "139.0", detail: "(约 ¥11.9 / 60min)" },
          business: { price: "499.0", originalPrice: "695.0", detail: "(约 ¥9.9 / 60min)" }
        }
      },
      plans: {
        basic: {
            name: "Free",
            desc: "适合个人用户轻度使用。"
        },
        pro: {
            name: "Pro",
            desc: "为追求极致效率的职场专业人士打造。"
        },
        team: {
            name: "Team",
            desc: "适用于需要团队协作与组织资产沉淀的场景。"
        }
      },
      features: {
        basic: [
          "每月 60 分钟处理时长",
          "顶级AI模型能力 (Powered by GPT-5.2, Claude 4.5 & Gemini 3.0 Pro)",
          "专为高质量会议设计的 Agent 能力",
          "智能音文双向溯源",
          "10 篇云端会议存储限制"
        ],
        pro: [
          "每月 1,000 分钟优先处理时长",
          "包含基础版全部能力",
          "即刻交付级 Word 导出",
          "无限量云端文档存储",
          "自定义 Agent 模板"
        ],
        team: [
          "包含所有专业版功能",
          "团队知识库: 支持跨会议 Agent 深度搜索",
          "共享 Agent 模板库: 统一全员交付标准",
          "管理员权限控制与团队效能分析报告"
        ]
      },
      addons: {
        title: "需要更多时长？",
        desc: "按需充值，永久有效。",
        single: "单次加油 (60m)",
        value: "超值包 (600m)",
        business: "商务包 (3000m)",
        popular: "最受欢迎"
      },
      roi: {
          title: "5倍生产力倍增器",
          desc: "一般来说，一小时的录音需要 5 小时整理. 使用 Minutes AI，您可以为每小时的会议节省 5 小时的整理时间。",
          manual: "人工整理",
          ai: "使用 Minutes AI",
          highlight: "每小时会议节省 5 小时"
      }
    },
    auth: {
      welcomeBack: "欢迎回来",
      welcomeSub: "用 AI 驱动的清晰度继续整理您的世界。",
      joinTitle: "Join Minutes AI",
      joinSub: "体验专业效率的新标准。",
      marketing: {
        zeroHallucinations: "零幻觉准确率",
        enterpriseSecurity: "企业级安全",
        unlimitedRecording: "无限录音时长"
      },
      login: "登录",
      createAccount: "创建账户",
      createSub: "开始 14 天免费试用. 无需信用卡。",
      email: "电子邮箱",
      phone: "手机号码",
      mobileNumber: "手机号码",
      verificationCode: "验证码",
      sendCode: "发送验证码",
      codeSent: "已发送",
      resendIn: "重新发送",
      password: "密码",
      setPassword: "设置密码",
      fullName: "全名",
      inviteCode: "邀请码 (选填)",
      getCode: "获赠 1 个月免费",
      orContinue: "或继续使用",
      noAccount: "还没有账户？",
      hasAccount: "已有账户？",
      signup: "注册",
      loginTabs: {
        password: "密码登录",
        emailCode: "邮箱验证码",
        phoneCode: "手机验证码"
      },
      accountPlaceholder: "邮箱 / 手机号 / 用户名",
      forgotPassword: "忘记密码?",
      forgotPasswordTitle: "重置密码",
      forgotPasswordDesc: "输入您的邮箱或手机号以接收验证码",
      resetMethod: {
        email: "邮箱验证",
        phone: "手机验证"
      },
      newPassword: "新密码",
      confirmPassword: "确认密码",
      resetPassword: "重置密码",
      backToLogin: "返回登录",
      passwordResetSuccess: "密码重置成功",
      whatsNew: "最新动态",
      march2025: "2025年 3月",
      systemsOperational: "系统运行正常",
      insightsWaiting: "您的会议洞察正在等您。",
      releaseNotes: "查看更新日志",
      consultingEngineTitle: "咨询级逻辑提取引擎 v2.4",
      consultingEngineDesc: "我们最新的推理引擎升级显著提高了技术术语的准确性和多发言人重叠检测能力。",
      testimonials: [
        {
          name: "Justin Ozanich",
          role: "市场经理",
          company: "Foster Coffee Company",
          text: "有了 Minutes AI，我可以根据需要提前构建社交媒体内容，如果发现行业内有某些趋势，还能随时调整营销活动。",
          avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=150&auto=format&fit=crop"
        },
        {
          name: "Elena Rodriguez",
          role: "法律顾问",
          company: "TechFlow Systems",
          text: "绝对的高保真度和零幻觉准确率让我有信心将这些摘要直接用于专业法律文件，而无需手动反复核对。",
          avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=150&auto=format&fit=crop"
        }
      ]
    },
    gateModal: {
      title: "立即加入 Minutes AI",
      subtitle: "使用我们丰富的模板集合，为您的工作创建无限的结构化会议纪要。",
      createAccount: "创建账户",
      orSignIn: "或登录"
    },
    inviteModal: {
        title: "邀请好友，各得 60 分钟",
        desc: "邀请好友注册，双方各得60分钟免费时长！",
        yourCode: "您的邀请码",
        tapToCopy: "点击复制",
        copied: "已复制",
        orShareLink: "或分享链接",
        terms: "适用条款与条件",
        referrals: "目前已有 0 人加入"
    },
    share: {
      title: "分享录音",
      publicAccess: "公开访问",
      publicDesc: "公开访问，任何拥有链接的人都可以查看此逐字稿。",
      privateDesc: "仅受邀团队成员可以访问。",
      copyLink: "复制链接",
      copied: "已复制",
      preview: "预览",
      previewTooltip: "查看访客视图",
      canView: "可查看",
      canEdit: "可编辑",
      disclaimer: "分享此链接即表示您同意我们关于内容分发的服务条款。"
    },
    public: {
      sharedBy: "分享者",
      join: "加入对话",
      joinDesc: "注册以编辑、评论 and 导出此会议。",
      signupFree: "免费注册",
      wantAction: "想要查看待办事项？",
      login: "登录",
      signup: "注册"
    }
  }
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: typeof translations.en;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children?: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('language');
    return saved === 'en' || saved === 'zh' ? saved : 'zh';
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t: translations[language] }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}